/**
 * The party room.
 *
 * Built against the reference (exec-3266bfab): who is here first, then the
 * conversation, with the party's details and channels in a rail. The rail sits
 * beside the room from 1280px and below it under that - three columns side by
 * side is exactly what squeezed Chat to 156px at 1092px. On a phone the room
 * is one view at a time, chosen with tabs.
 *
 * Nothing here was removed: channels, officers, ownership, kicking, voice
 * force-mute, chat mute, per-person volume, nicknames, boost and edit all
 * work as before. What changed is where they live - host actions moved from
 * hover-only icons on a row into a menu that is always visible, and the call
 * controls sit beside the room as well as in the floating dock, because the
 * dock can be dragged away or popped out.
 *
 * Deliberately not built from the reference: the ready check, which has no
 * backend, and a shareable invite link, which does not exist - invites are
 * sent to people, so "Invite friends" opens that instead.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { copyText } from '@/lib/clipboard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints, socket } from '@/lib/api';
import type { Message, Party, PartyAddonState, PartyMember } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { useVoice } from '@/stores/voice';
import { useLocal } from '@/stores/local';
import { Avatar } from '@/components/Avatar';
import { ChatLog, Composer } from '@/components/chat';
import { UserName, openUserCard } from '@/components/user';
import { Button, Empty, Field, Input, Modal, Toggle, useConfirm, useToast } from '@/components/ui';
import { InvitePeople } from '@/components/Invites';
import { ScreenStage } from '@/components/ScreenStage';
import { TypingLine } from '@/components/TypingLine';
import { Select } from '@/components/Select';
import { type Channel } from '@/components/Channels';
import { MenuPanel, useContextMenu, type MenuItem } from '@/components/Menu';
import { PartyPermissions } from '@/components/PartyPermissions';
import { Tip } from '@/components/HoverCard';
import { Icon } from '@/icons/Icon';
import { useMinWidth } from '@/lib/viewport';
import { PartyAddonLauncher, PartyAddonStage, PartyAddons, mergePartyAddon, runningAddons } from '@/components/PartyAddons';
import { useSplitter } from '@/lib/splitter';
import { Grip } from '@/components/Grip';
import { ago } from '@/lib/notifications';
import { gameBanner } from '@/lib/gameArt';
import { useNarrow } from '@/components/TabBar';
import { EmojiText } from '@/components/EmojiText';
import { EmojiField } from '@/components/EmojiField';
import { ReadyCheck, SharedGames } from '@/components/ReadyCheck';
import { BackSoon, LobbyCard, RaiseHand, WaitingList } from '@/components/PartyExtras';
import { PartyLog } from '@/components/Safety';
import { PartyClips } from '@/components/Sessions';
import { WatchTogether } from '@/components/WatchTogether';
import { MicCheck, micCheckWanted } from '@/components/MicCheck';
import { NumberField } from '@/components/NumberField';

/*
 * The chat's share of the room.
 *
 * The chat is pinned to the bottom, above the call bar, and fills whatever is
 * left. Dragging its top edge up asks for more; the room above makes way in
 * steps instead of the page scrolling - the party card drops to one line, the
 * members to small tiles, then to a strip of pictures.
 */
const CHAT_MIN_H = 200;
const CHAT_DEFAULT_H = 300;
/* Everything above the chat in each arrangement, roomiest first: the back
   row, the party card, the members, the gaps, the "Party chat" heading and
   the column's bottom padding. Measured from the rendered room. */
const ROOM_FIXED = 36 + 12 + 30 + 12 + 12;
const LAYOUTS = [
  { hero: 'full', members: 'cards', used: ROOM_FIXED + 112 + 12 + 201 },
  { hero: 'full', members: 'tiles', used: ROOM_FIXED + 112 + 12 + 110 },
  { hero: 'mini', members: 'tiles', used: ROOM_FIXED + 56 + 12 + 110 },
  { hero: 'mini', members: 'strip', used: ROOM_FIXED + 56 + 12 + 44 }
] as const;

export function PartyView({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const me = useSession((s) => s.me);
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const voice = useVoice();
  const [editing, setEditing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const narrow = useNarrow();
  const catalog = useSession((s) => s.catalog);
  /*
   * On a phone, one view at a time. Tabs with words on them, rather than the
   * three unlabelled header toggles that used to switch these.
   */
  const [mobileTab, setMobileTab] = useState<'chat' | 'members' | 'details'>('chat');
  /* The rail sits beside the room on wide screens and below it otherwise. */
  const railBeside = useMinWidth(1024);
  const [heroMenu, setHeroMenu] = useState<{ x: number; y: number } | null>(null);
  const [perms, setPerms] = useState(false);

  /* One chat now - the channel list is gone - so this never changes. */
  const [textChannel] = useState('general');
  // The socket effect is subscribed once; a ref lets it read the current
  // channel without re-subscribing every time you switch.
  const textChannelIdRef = useRef(id);
  const [managing, setManaging] = useState<{ channel: Channel | null } | null>(null);
  const [tuning, setTuning] = useState<{ id: string; name: string } | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const nicknames = useLocal((s) => s.nicknames);
  const setNickname = useLocal((s) => s.setNickname);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  /* The rail can be dragged narrower or wider; the room keeps the rest. */
  const railSplit = useSplitter({ key: 'pogu.party.rail', initial: 340, min: 280, max: 560, side: 'left' });

  /* How tall the chat is asked to be; the room decides how to make space. */
  const chatBox = useRef<HTMLDivElement>(null);
  const [chatWant, setChatWant] = useState(() => {
    const saved = Number(localStorage.getItem('pogu.party.chatWant'));
    return Number.isFinite(saved) && saved >= CHAT_MIN_H ? saved : CHAT_DEFAULT_H;
  });
  const [chatDragging, setChatDragging] = useState(false);
  /* The big area shows the chat or the party's addons. */
  /* Chat, addons, or both side by side - remembered. */
  const [roomTab, setRoomTabState] = useState<'chat' | 'addons' | 'split'>(() => {
    try {
      const saved = localStorage.getItem('pogu.party.room');
      return saved === 'split' || saved === 'addons' ? saved : 'chat';
    } catch { return 'chat'; }
  });
  const setRoomTab = (t: 'chat' | 'addons' | 'split') => {
    setRoomTabState(t);
    try { localStorage.setItem('pogu.party.room', t); } catch { /* storage off */ }
  };
  const [stageKey, setStageKey] = useState<string | null>(null);
  useEffect(() => {
    try { localStorage.setItem('pogu.party.chatWant', String(Math.round(chatWant))); } catch { /* storage off */ }
  }, [chatWant]);
  const [columnH, setColumnH] = useState(0);
  const columnWatch = useRef<ResizeObserver | null>(null);
  const columnRef = useCallback((col: HTMLDivElement | null) => {
    columnWatch.current?.disconnect();
    columnWatch.current = null;
    if (!col) return;
    const measure = () => setColumnH(col.clientHeight);
    const watch = new ResizeObserver(measure);
    watch.observe(col);
    columnWatch.current = watch;
    measure();
  }, []);
  /* The roomiest arrangement that still gives the chat what was asked for. */
  let level = LAYOUTS.length - 1;
  for (let i = 0; i < LAYOUTS.length; i++) {
    if (columnH - LAYOUTS[i]!.used >= chatWant) { level = i; break; }
  }
  const chatMax = Math.max(CHAT_MIN_H, columnH - LAYOUTS[LAYOUTS.length - 1]!.used);

  function startChatResize(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    const node = chatBox.current;
    if (!node) return;
    e.preventDefault();
    const grip = e.currentTarget;
    grip.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    const startH = node.getBoundingClientRect().height;
    let pointerY = startY;
    let frame = 0;
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    setChatDragging(true);
    /* Up is bigger: the top edge follows the pointer. */
    const apply = () => {
      frame = 0;
      setChatWant(Math.round(Math.max(CHAT_MIN_H, Math.min(chatMax, startH + (startY - pointerY)))));
    };
    const move = (ev: PointerEvent) => { pointerY = ev.clientY; if (!frame) frame = requestAnimationFrame(apply); };
    const up = () => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      if (frame) { cancelAnimationFrame(frame); apply(); }
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = '';
      setChatDragging(false);
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  }

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['party', id],
    queryFn: () => endpoints.party(id)
  });

  const party = data?.party;

  const [messages, setMessages] = useState<Message[]>([]);

  // The default channel comes with the party payload; any other one is
  // fetched when you pick it.
  const channelMessages = useQuery({
    queryKey: ['party-messages', id, textChannel],
    queryFn: () => api.get<{ messages: Message[] }>(
      '/api/messages/party/' + encodeURIComponent(textChannel === 'general' ? id : id + '#' + textChannel)
    ),
    enabled: textChannel !== 'general'
  });

  useEffect(() => {
    if (textChannel === 'general') setMessages(data?.messages ?? []);
    else setMessages(channelMessages.data?.messages ?? []);
  }, [textChannel, data?.messages, channelMessages.data]);

  /* live */
  useEffect(() => {
    const off = [
      socket.on('message:new', (msg: Message) => {
        if (msg.scope !== 'party') return;
        const [partyId] = msg.channelId.split('#');
        if (partyId !== id) return;
        // Only the channel on screen appends; the rest are fetched when opened.
        if (msg.channelId !== textChannelIdRef.current) return;
        setMessages((list) => [...list, msg]);
      }),
      socket.on('message:edited', (msg: Message) => {
        if (msg.channelId !== id) return;
        setMessages((list) => list.map((m) => (m.id === msg.id ? msg : m)));
      }),
      socket.on('message:reaction', (d: { messageId: string; channelId: string; reactions: Record<string, string[]> }) => {
        if (d.channelId !== id) return;
        setMessages((list) => list.map((m) => (m.id === d.messageId ? { ...m, reactions: d.reactions } : m)));
      }),
      socket.on('message:deleted', (d: { messageId: string; channelId: string }) => {
        if (d.channelId !== id) return;
        setMessages((list) => list.filter((m) => m.id !== d.messageId));
      }),
      socket.on('party:updated', (d: { party: Party }) => {
        if (d.party.id !== id) return;
        queryClient.setQueryData(['party', id], (old: typeof data) => old && { ...old, party: { ...old.party, ...d.party } });
      }),
      socket.on('party:closed', (d: { partyId: string; reason?: string }) => {
        if (d.partyId !== id) return;
        // Say which kind of closed it was. A party that timed out because
        // its host left is a different thing from one somebody shut.
        toast(
          d.reason === 'abandoned'
            ? 'This party closed - the host left and nobody was in it'
            : 'This party was closed',
          'warn'
        );
        go('parties');
      }),
      socket.on('party:addon', (d: { partyId: string; key: string; addon: PartyAddonState | null }) => {
        if (d.partyId !== id) return;
        mergePartyAddon(queryClient, id, d.key, d.addon);
      }),
      /* The host opening or locking the addons reaches everybody at once:
         a member holding a stale 'locked' would have their presses quietly
         refused by the server with nothing on screen to explain it. */
      socket.on('party:addons-open', (d: { partyId: string; addonsOpen: boolean }) => {
        if (d.partyId !== id) return;
        void queryClient.invalidateQueries({ queryKey: ['party', id] });
      }),
      socket.on('party:member_joined', () => queryClient.invalidateQueries({ queryKey: ['party', id] })),
      socket.on('party:member_left', () => queryClient.invalidateQueries({ queryKey: ['party', id] }))
    ];
    return () => off.forEach((fn) => fn());
  }, [id, queryClient, toast]);

  if (isPending) {
    return <PartySkeleton />;
  }
  if (isError || !party) {
    /*
     * Why it is not here, in words a player can act on. A party that ended, a
     * party you were not invited to, and a request that failed are three
     * different situations with three different next steps.
     */
    const message = (error as Error)?.message ?? '';
    const ended = /no longer exists|not found|is gone/i.test(message);
    const privateParty = /invite only/i.test(message);
    return (
      <div className="mx-auto w-full max-w-[640px] px-4 py-10">
        <Empty
          role={ended || privateParty ? 'status' : 'alert'}
          title={ended ? 'This party has ended' : privateParty ? 'This party is invite only' : 'Could not open this party'}
          note={ended
            ? 'The host closed it, or everyone left. There are other parties running.'
            : privateParty
              ? 'Only people the host invited can see it. Ask them for an invite.'
              : 'Something went wrong loading it. Everything else is still working.'}
          actions={
            <>
              {!ended && !privateParty ? (
                <Button kind="outline" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Trying…' : 'Try again'}</Button>
              ) : null}
              <Button kind="primary" onClick={() => go('parties')}>Browse parties</Button>
            </>
          }
        />
      </div>
    );
  }

  const myRole: 'owner' | 'officer' | 'staff' | 'member' | null =
    !me ? null
      : party.hostId === me.id ? 'owner'
        : party.members.find((m) => m.userId === me.id)?.role === 'officer' ? 'officer'
          : me.role !== 'user' ? 'staff'
            : party.members.some((m) => m.userId === me.id) ? 'member' : null;

  const isOwner = myRole === 'owner';
  const canManage = myRole === 'owner' || myRole === 'officer' || myRole === 'staff';
  const inVoice = new Set(voice.rooms[party.id] ?? party.voice?.members ?? []);
  const connectedHere = voice.partyId === party.id;
  const owner = party.members.find((m) => m.userId === party.hostId)?.user ?? null;

  // 'general' is the party id itself, so everything written before channels
  // existed still lives there. Other channels hang off it with a suffix.
  const textChannelId = textChannel === 'general' ? id : id + '#' + textChannel;
  textChannelIdRef.current = textChannelId;

  async function act(path: string, body?: unknown, note?: string) {
    try {
      const res = await api.post<{ party?: Party }>(path, body);
      if (note) toast(note, 'good');

      /*
       * The answer is the new party, so take it.
       *
       * Invalidating instead meant a second round trip whose result replaced
       * every object in the tree, re-rendering the whole screen a beat after
       * the socket had already delivered the same change. Boosting looked
       * like the party reloading itself.
       */
      if (res?.party) {
        queryClient.setQueryData(['party', id], (old: typeof data) => (old ? { ...old, party: res.party! } : old));
      } else {
        void queryClient.invalidateQueries({ queryKey: ['party', id] });
      }
    } catch (err) { toast((err as Error).message, 'bad'); }
  }

  /**
   * What you can do to somebody in this party.
   *
   * The party-specific powers first - they are why you right-clicked - then
   * the ordinary things you can do to any player anywhere.
   */
  function partyMemberMenu(user: { id: string; username: string; displayName: string }): MenuItem[] {
    const items: MenuItem[] = [];
    if (!party || user.id === me?.id) return standardItems(user);

    const theirRole = party.members.find((m) => m.userId === user.id)?.role;
    const theyHost = party.hostId === user.id;

    if (canManage && !theyHost) {
      items.push({
        label: theirRole === 'officer' ? 'Remove officer' : 'Make officer',
        icon: 'shieldNav',
        onClick: () => void act(
          '/api/parties/' + id + '/role',
          { userId: user.id, role: theirRole === 'officer' ? 'member' : 'officer' },
          theirRole === 'officer' ? user.displayName + ' is no longer an officer' : user.displayName + ' is an officer'
        )
      });
    }

    if (isOwner && !theyHost) {
      items.push({
        label: 'Give ownership',
        icon: 'crown',
        onClick: () => {
          void (async () => {
            const ok = await confirm(
              'Hand the party to ' + user.displayName + '?',
              'They become the host and you become a member. You cannot undo this yourself.',
              { confirmLabel: 'Give ownership', danger: true }
            );
            if (ok) void act('/api/parties/' + id + '/owner', { userId: user.id }, user.displayName + ' owns the party now');
          })();
        }
      });
    }

    if (canManage && !theyHost) {
      items.push({
        label: 'Kick from party',
        icon: 'logout',
        danger: true,
        onClick: () => {
          void (async () => {
            const ok = await confirm('Kick ' + user.displayName + '?', 'They drop out of voice too, and can rejoin unless the party is full.', { confirmLabel: 'Kick', danger: true });
            if (ok) void act('/api/parties/' + id + '/kick', { userId: user.id }, user.displayName + ' was kicked');
          })();
        }
      });
    }

    /*
     * What a host or an officer can do to them.
     *
     * Kept apart from the two below it, which are your own listening
     * settings - one silences somebody for everybody, the other only for you,
     * and confusing the two is how people get muted by accident.
     */
    if (canManage && user.id !== me?.id && user.id !== party.hostId) {
      const inCall = inVoiceWithMe(user.id) || (voice.roomStates[party.id] ?? []).some((m) => m.userId === user.id);
      const forced = (voice.roomStates[party.id] ?? []).find((m) => m.userId === user.id)?.forceMuted;
      const member = party.members.find((m) => m.userId === user.id);
      const chatMuted = !!member?.mutedUntil && member.mutedUntil > Date.now();

      if (items.length) items.push('-');
      if (inCall) {
        items.push({
          label: forced ? 'Let them speak' : 'Mute in voice',
          icon: forced ? 'mic' : 'micOff',
          onClick: () => socket.send('voice:force', { partyId: party.id, userId: user.id, muted: !forced })
        });
      }
      items.push({
        label: chatMuted ? 'Let them post' : 'Mute in chat for 15 minutes',
        icon: 'chat',
        onClick: () => void act(
          '/api/parties/' + party.id + '/members/' + user.id + '/mute',
          { muted: !chatMuted, minutes: 15 },
          chatMuted ? user.displayName + ' can post again' : user.displayName + ' muted for 15 minutes'
        )
      });
    }

    // Your own listening settings for this person. Local to you: they are
    // never told, and nobody else in the call is affected.
    if (inVoiceWithMe(user.id)) {
      if (items.length) items.push('-');
      const level = voice.levels[user.id] ?? 1;
      items.push({
        label: level === 0 ? 'Unmute for me' : 'Mute for me',
        icon: level === 0 ? 'mic' : 'micOff',
        onClick: () => voice.setLevel(user.id, level === 0 ? 1 : 0)
      });
      items.push({
        label: 'Volume - ' + Math.round(level * 100) + '%',
        icon: 'headset',
        onClick: () => setTuning({ id: user.id, name: user.displayName })
      });
    }

    if (items.length) items.push('-');
    items.push({
      label: nicknames[user.id] ? 'Change nickname' : 'Set a nickname',
      icon: 'edit',
      onClick: () => setRenaming({ id: user.id, name: user.displayName })
    });

    return [...items, ...standardItems(user)];
  }

  /** Whether they are in the same voice channel as you right now. */
  function inVoiceWithMe(userId: string): boolean {
    return voice.partyId === id && (voice.members ?? []).some((m) => m.userId === userId);
  }

  /** Profile, message, friend - the same anywhere a person appears. */
  function standardItems(user: { id: string; username: string; displayName: string }): MenuItem[] {
    const items: MenuItem[] = [
      { label: 'View full profile', icon: 'profile', onClick: () => go('u/' + user.username) }
    ];
    if (user.id !== me?.id) {
      items.push({ label: 'Message', icon: 'chat', onClick: () => go('chat/' + user.username) });
    }
    items.push({
      label: 'Copy username',
      icon: 'copy',
      onClick: () => {
        void copyText(user.username);
        toast('Username copied', 'good');
      }
    });
    if (me && me.role !== 'user') {
      items.push({ label: 'Open in staff console', icon: 'shieldNav', onClick: () => go('staff?user=' + user.username) });
    }
    return items;
  }

  async function leave() {
    /* The host is told what happens to the party - leaving is also handing it on. */
    const alone = (party?.memberCount ?? 1) <= 1;
    const note = isOwner
      ? (alone
        ? 'You are the only one here, so the party closes when you leave.'
        : 'You are the host, so the party passes to the longest-standing member. You drop out of voice too.')
      : 'You drop out of the voice channel too. You can rejoin while there is a free slot.';
    const ok = await confirm('Leave party?', note, { confirmLabel: 'Leave', danger: true });
    if (!ok) return;
    try {
      if (connectedHere) await voice.leave();
      await api.post(`/api/parties/${id}/leave`);
    } catch (err) {
      /* Stay in the room rather than pretending it worked. */
      toast((err as Error).message || 'Could not leave the party. Try again.', 'bad');
      return;
    }
    // The nav uses this list to decide where 'Parties' goes; leave it stale
    // and the tab walks you straight back into the party you just left.
    await queryClient.invalidateQueries({ queryKey: ['parties'] });
    go('parties');
  }

  const game = catalog?.games.find((g) => g.id === party.gameId);
  const regionName = catalog?.regions.find((r) => r.id === party.region)?.name ?? party.region;
  const full = party.memberCount >= party.size;
  const isStaff = !!me && me.role !== 'user';
  const banner = gameBanner(party.gameId);

  const heroItems: MenuItem[] = [];
  if (canManage) heroItems.push({ label: 'Edit party', icon: 'sliders', onClick: () => setEditing(true) });
  if (canManage) heroItems.push({ label: 'What has been done here', icon: 'history', onClick: () => setLogOpen(true) });
  /* Who can do what: the ranks and the five switches, in one sheet. */
  if (party.isMember) heroItems.push({ label: 'Who can do what', icon: 'shield', onClick: () => setPerms(true) });
  /*
   * Keeping tonight's group without making it a crew.
   *
   * A good evening with four people does not mean any of you want a permanent
   * shared identity - but it does mean you want to be able to say "same time
   * next week" without adding everybody as a friend first. A group chat is
   * exactly that much commitment, and it outlives the party.
   */
  if (party.isMember && party.memberCount > 1) {
    heroItems.push({
      label: 'Keep this group in a chat',
      icon: 'users',
      onClick: () => {
        void (async () => {
          try {
            const res = await api.post<{ group: { id: string; name: string } }>('/api/groups', {
              name: party.title,
              members: party.members
                .filter((m) => m.userId !== me?.id)
                .map((m) => m.user?.username)
                .filter((name): name is string => !!name)
            });
            toast('The group is yours - it outlives the party', 'good');
            go('chat/g/' + res.group.id);
          } catch (err) {
            toast((err as Error).message, 'bad');
          }
        })();
      }
    });
  }
  if (isStaff) {
    heroItems.push({
      label: party.boosted ? 'Remove boost' : 'Boost to the top of every list',
      icon: 'bolt',
      onClick: () => void act('/api/admin/parties/' + id + '/boost', { boosted: !party.boosted },
        party.boosted ? 'Boost removed' : 'Boosted to the top of every list')
    });
  }
  heroItems.push({
    label: 'Copy link',
    icon: 'link',
    onClick: () => { void copyText(location.origin + '/#/party/' + id); toast('Link copied', 'good'); }
  });

  const members = [...party.members].sort((a, b) => rank(a, party) - rank(b, party) || a.joinedAt - b.joinedAt);
  const free = Math.max(0, party.size - party.memberCount);

  /* One voice channel and one chat - the reference has no channel list, and
     a room of four friends never needed three voice rooms to choose from. */
  const rail = (
    <PartyRail
      party={party}
      shared={party.isMember ? (
        <>
          <LobbyCard party={party} canManage={canManage} />
          <WatchTogether scope="party" channelId={party.id} canStart={party.isMember} />
          <PartyClips party={party} />
          <WaitingList party={party} />
          {party.memberCount > 1 ? <SharedGames party={party} /> : null}
        </>
      ) : null}
      gameName={game?.name ?? party.gameId}
      regionName={regionName}
      owner={owner}
      fill={!narrow && railBeside}
      onInvite={party.isMember ? () => setInviting(true) : undefined}
      addons={party.isMember ? (!narrow && railBeside ? (
        <PartyAddonLauncher
          party={party}
          canManage={canManage}
          activeKey={roomTab !== 'chat' ? (stageKey ?? runningAddons(party)[0]?.id ?? null) : null}
          onOpen={(key) => { setStageKey(key); if (roomTab === 'chat') setRoomTab('addons'); }}
        />
      ) : <PartyAddons party={party} canManage={canManage} />) : null}
    />
  );

  /*
   * Desktop fits the window: no page scroll, as in the reference. The hero and
   * the member row keep their size, the chat takes whatever height is left and
   * scrolls inside itself, the rail scrolls on its own if it must, and the
   * call bar sits along the bottom. Phones keep a scrolling page - there is no
   * fitting all of this into 390 by 844.
   */
  const fit = !narrow && railBeside;
  /* The desktop room is always the compact one; the arrangement sets how much
     of it the party card and the members take. */
  const tight = fit;
  const heroMini = fit && LAYOUTS[level]!.hero === 'mini';
  const memberMode: 'grid' | 'cards' | 'tiles' | 'strip' = fit ? LAYOUTS[level]!.members : 'grid';

  const hero = (
    <section aria-label="Party" className="relative isolate flex-none overflow-hidden rounded-[var(--radius-xl)] bg-surface-3 shadow-[var(--shadow-1)]">
      {banner ? (
        <img src={banner} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover" />
      ) : (
        <span aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(125deg, ' + (game?.color ?? 'var(--accent)') + ', var(--art-base) 85%)' }} />
      )}
      <span aria-hidden className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(14,11,8,.78)_0%,rgba(14,11,8,.45)_50%,rgba(14,11,8,.05)_100%)]" />

      <div className={cn('flex flex-col items-stretch text-white sm:flex-row sm:items-end', tight ? (heroMini ? 'gap-3 px-4 py-2 sm:flex-nowrap sm:items-center' : 'gap-3 p-4 sm:flex-nowrap sm:items-center sm:p-4') : 'sm:flex-wrap', tight ? '' : 'gap-4 p-5 sm:p-6', tight ? 'min-h-0' : fit ? 'min-h-[168px]' : 'min-h-[176px]')}>
        <div className="min-w-0 flex-1">
          <h1 className={cn('m-0 font-extrabold tracking-[-.03em] drop-shadow-sm', heroMini ? 'line-clamp-1 text-[17px]' : tight ? 'line-clamp-1 text-[22px] sm:text-[24px]' : 'line-clamp-2 text-[26px] sm:text-[32px]', 'leading-tight')}>
            <EmojiText text={party.title} size={28} />
          </h1>
          <p className={cn('mb-0 text-white/90', heroMini ? 'truncate text-[12.5px]' : tight ? 'mt-1 truncate text-[14px]' : 'mt-1.5 text-[16px]')}>
            {(heroMini ? [party.mode, regionName, party.memberCount + '/' + party.size + ' players'] : [party.mode, regionName, party.language]).filter(Boolean).join('  ·  ')}
          </p>
          {heroMini ? null : <p className={cn('mb-0 flex items-center gap-x-4 gap-y-1 font-semibold', tight ? 'mt-1 flex-nowrap overflow-hidden whitespace-nowrap text-[14px]' : 'flex-wrap', tight ? '' : 'mt-2 text-[16px]')}>
            <span className="flex items-center gap-2"><Icon name="users" size={18} />{party.memberCount}/{party.size} players{full ? ' · Full' : ''}</span>
            {party.micRequired ? <span className="flex min-w-0 items-center gap-1.5 font-medium text-white/85"><Icon name="mic" size={16} className="flex-none" /><span className="truncate">Mic required</span></span> : null}
          </p>}
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {party.isMember ? (
            <Button kind="primary" icon={<Icon name="addFriend" size={18} />} onClick={() => setInviting(true)} className={cn(heroMini ? 'h-9 px-3.5 text-[14px]' : tight ? 'h-10 px-4 text-[15px]' : 'h-[48px] px-5 text-[16px]', 'shadow-[0_8px_20px_-8px_rgba(79,70,229,.8)]')}>
              Invite friends
            </Button>
          ) : full ? (
            <Button kind="plain" disabled className={cn(heroMini ? 'h-9 px-3.5 text-[14px]' : tight ? 'h-10 px-4 text-[15px]' : 'h-[48px] px-5 text-[16px]')}>Party is full</Button>
          ) : (
            <Button kind="primary" onClick={() => void act('/api/parties/' + id + '/join', {}, 'Joined')} className={cn(heroMini ? 'h-9 px-3.5 text-[14px]' : tight ? 'h-10 px-4 text-[15px]' : 'h-[48px] px-5 text-[16px]')}>Join party</Button>
          )}
          <button
            type="button"
            aria-label="More party actions"
            aria-haspopup="menu"
            onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHeroMenu({ x: Math.max(8, r.right - 214), y: r.bottom + 6 }); }}
            className={cn(heroMini ? 'h-9 px-3.5 text-[14px]' : tight ? 'h-10 px-4 text-[15px]' : 'h-[48px] px-5 text-[16px]', 'flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-white font-bold text-[#2a2520] outline-none transition-[background-color,transform] duration-[var(--t-hover)] ease-[var(--ease)] hover:bg-white/90 active:scale-[.98] focus-visible:shadow-[0_0_0_3px_var(--accent)]')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
            More
          </button>
        </div>
      </div>
    </section>
  );

  const seats = party.isMember ? Array.from({ length: Math.min(2, free) }) : [];
  const memberRow = memberMode === 'strip' ? (
    /* The smallest arrangement: one line of pictures, still with the menus. */
    <section aria-labelledby="party-members-title" className="flex h-11 flex-none items-center gap-3">
      <h2 id="party-members-title" className="m-0 flex-none text-[14px] font-bold text-ink">
        Members <span className="font-semibold text-mute">{party.memberCount}/{party.size}</span>
      </h2>
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto">
        {members.map((member) => member.user ? (
          <button
            key={member.userId}
            type="button"
            onClick={(e) => openUserCard(member.user!, e.currentTarget)}
            aria-label={'Show ' + member.user.displayName + "'s profile card"}
            className={cn(
              'flex-none rounded-full p-[2px] outline-none transition-[box-shadow] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
              (voice.roomStates[party.id] ?? []).some((v) => v.userId === member.userId && v.speaking && !v.muted) ? 'shadow-[0_0_0_2px_var(--good)]' : ''
            )}
          >
            <Avatar user={member.user} size={34} presence />
          </button>
        ) : null)}
        {seats.map((_, i) => (
          <button
            key={'seat' + i}
            type="button"
            onClick={() => setInviting(true)}
            aria-label="Invite a friend to an open seat"
            className="grid h-[38px] w-[38px] flex-none place-items-center rounded-full border-2 border-dashed border-line text-mute outline-none transition-colors hover:border-accent-line hover:text-accent focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            <Icon name="plus" size={15} />
          </button>
        ))}
      </div>
    </section>
  ) : (
    <section aria-labelledby="party-members-title" className="flex-none">
      <h2 id="party-members-title" className={cn('m-0 font-bold tracking-[-.02em] text-ink', tight ? 'mb-2 text-[16px]' : 'mb-3 text-[20px]')}>
        Party members ({party.memberCount}/{party.size})
      </h2>
      {/* One row on desktop that scrolls sideways if the party is big, so the
          page itself never has to. */}
      <div className={cn(memberMode === 'tiles' ? 'flex gap-2.5 overflow-x-auto pb-1 [&>*]:w-[200px] [&>*]:flex-none' : memberMode === 'cards' ? 'flex gap-3 overflow-x-auto pb-1 [&>*]:w-[150px] [&>*]:flex-none' : 'grid grid-cols-2 gap-3 sm:grid-cols-3')}>
        {members.map((member) => (
          <MemberCard
            key={member.userId}
            member={member}
            party={party}
            inVoice={inVoice.has(member.userId)}
            voiceState={(voice.roomStates[party.id] ?? []).find((v) => v.userId === member.userId)}
            menu={partyMemberMenu}
            compact={memberMode === 'tiles'}
          />
        ))}
        {seats.map((_, i) => {
          const small = memberMode === 'tiles';
          return (
            <button
              key={'seat' + i}
              type="button"
              onClick={() => setInviting(true)}
              className={cn(small ? 'flex min-h-[64px] items-center gap-3 px-3 text-left' : 'flex min-h-[150px] flex-col items-center justify-center gap-2 px-3 text-center', 'rounded-[var(--radius-l)] border-2 border-dashed border-line bg-transparent outline-none transition-[border-color,background-color] duration-[var(--t-hover)] ease-[var(--ease)] hover:border-accent-line hover:bg-accent-soft focus-visible:shadow-[0_0_0_2px_var(--accent)]')}
            >
              <span className={cn('grid flex-none place-items-center rounded-full bg-surface-3 text-dim', small ? 'h-10 w-10' : 'h-12 w-12')}><Icon name="plus" size={small ? 18 : 22} /></span>
              <span className={cn(small && 'flex min-w-0 flex-col')}>
                <span className={cn('block font-semibold text-ink', small ? 'text-[14px]' : 'text-[15px]')}>Open seat</span>
                <span className={cn('block text-mute', small ? 'text-[12px]' : 'text-[13px]')}>Invite a friend</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );

  const chat = (
    <section
      aria-labelledby="party-chat-title"
      className={cn('flex flex-col', fit ? 'min-h-0 flex-1' : '')}
    >
      <div className={cn('flex flex-none items-center gap-3', tight ? 'mb-1.5 h-6' : 'mb-3')}>
        {fit && party.isMember ? (
          <div role="tablist" aria-label="Party room" className="flex flex-none items-center gap-1">
            <h2 id="party-chat-title" className="sr-only">Party chat</h2>
            {(['chat', 'addons', 'split'] as const).map((t) => {
              const n = t === 'addons' ? runningAddons(party).length : 0;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={roomTab === t}
                  onClick={() => setRoomTab(t)}
                  className={cn(
                    'flex h-6 items-center gap-1.5 rounded-[calc(8px*var(--radius-mul,1))] px-2.5 text-[14px] font-bold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    roomTab === t ? 'bg-surface-2 text-ink shadow-[inset_0_0_0_1px_var(--line-soft)]' : 'text-mute hover:text-ink'
                  )}
                >
                  {t === 'chat' ? 'Party chat' : t === 'addons' ? 'Addons' : <><Icon name="layers" size={14} />Both</>}
                  {n ? <span className="rounded-full bg-accent-soft px-1.5 text-[11px] leading-[16px] text-accent tabular-nums">{n}</span> : null}
                </button>
              );
            })}
          </div>
        ) : (
          <h2 id="party-chat-title" className={cn('m-0 font-bold tracking-[-.02em] text-ink', tight ? 'text-[16px]' : 'text-[20px]')}>Party chat</h2>
        )}
        {/* Drag this to give the chat more of the room, or less. */}
        {fit ? (
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize the party chat. Drag up for more, or use the up and down arrow keys."
            aria-valuemin={CHAT_MIN_H}
            aria-valuemax={chatMax}
            aria-valuenow={Math.round(chatWant)}
            tabIndex={0}
            title="Drag up to make the chat bigger · double-click to reset"
            onPointerDown={startChatResize}
            onDoubleClick={() => setChatWant(CHAT_DEFAULT_H)}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
              e.preventDefault();
              const now = chatBox.current?.getBoundingClientRect().height ?? chatWant;
              setChatWant(Math.max(CHAT_MIN_H, Math.min(chatMax, Math.round(now) + (e.key === 'ArrowUp' ? 80 : -80))));
            }}
            className="group/ch flex h-6 flex-1 cursor-row-resize touch-none items-center justify-center outline-none"
          >
            <span className={cn(
              'flex h-[8px] items-center justify-center gap-[5px] rounded-full px-3 transition-[background-color,width,box-shadow] duration-[250ms]',
              chatDragging ? 'bg-accent-soft shadow-[0_0_0_1px_var(--accent-line)]' : 'bg-surface-2 shadow-[0_0_0_1px_var(--line-soft)] group-hover/ch:bg-surface-3 group-focus-visible/ch:shadow-[0_0_0_2px_var(--accent)]'
            )}>
              {[0, 1, 2].map((i) => (
                <i key={i} aria-hidden className={cn('block h-[4px] w-[4px] rounded-full transition-colors duration-200', chatDragging ? 'bg-accent' : 'bg-mute group-hover/ch:bg-ink')} />
              ))}
            </span>
          </div>
        ) : null}
      </div>
      {/* Chat and addons share the big area: one or the other, or both
          side by side so you can talk while you use an addon. */}
      <div className={cn(fit ? 'flex min-h-0 flex-1 gap-3' : 'contents')}>
      <div
        ref={chatBox}
        className={cn(
          'pugo-panel smooth-clip flex flex-col overflow-hidden rounded-[calc(20px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]',
          fit ? 'min-h-0 min-w-0 flex-1' : 'h-[calc(100dvh-300px)] min-h-[360px]',
          chatDragging && 'shadow-[inset_0_0_0_1px_var(--accent-line)]',
          fit && roomTab === 'addons' && 'hidden'
        )}
      >
        {party.textEnabled === false ? (
          <div className="grid flex-1 place-items-center p-6">
            <Empty title="Text chat is off" note="The host turned text chat off for this party. Voice still works." />
          </div>
        ) : (
          <ChatLog
            messages={messages}
            canModerate={canManage}
            onReply={setReplyTo}
            emptyHint="Say hello — parties fill faster when someone talks first."
          />
        )}

        {party.isMember && party.textEnabled !== false ? (
          <>
            <TypingLine match={(p) => p.scope === 'party' && p.channelId === textChannelId} />
            <Composer
              focusKey={textChannel}
              poll={{ scope: 'party', channelId: textChannelId }}
              people={party.members.map((m) => m.user).filter((u): u is NonNullable<typeof u> => !!u)}
              placeholder="Message the party…"
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              onSend={async (body, attachments, reply, spoilers) => {
                await api.post('/api/messages', { scope: 'party', channelId: textChannelId, body, attachments, replyTo: reply, spoilers });
              }}
              onTyping={() => socket.send('typing', { scope: 'party', channelId: textChannelId })}
            />
          </>
        ) : !party.isMember ? (
          <p className="m-0 border-t border-line-soft px-4 py-3 text-[14px] text-mute">
            {full ? 'This party is full, so only its members can chat.' : 'Join the party to chat with everyone here.'}
          </p>
        ) : null}
      </div>
      {fit && party.isMember ? (
        <PartyAddonStage party={party} canManage={canManage} selected={stageKey} onSelect={setStageKey} hidden={roomTab === 'chat'} />
      ) : null}
      </div>
    </section>
  );

  const topRow = (
    <div className="flex flex-none items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => go('parties')}
        className="-ml-1 flex items-center gap-2 rounded-[calc(8px*var(--radius-mul,1))] px-1 py-1 text-[16px] font-semibold text-dim outline-none transition-colors duration-[var(--t-hover)] ease-[var(--ease)] hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M20 12H5M11 18l-6-6 6-6" /></svg>
        Back to parties
      </button>
      {party.isMember ? (
        <button
          type="button"
          onClick={() => void leave()}
          className={cn(tight ? 'h-9 text-[14px]' : 'h-[44px] text-[15px]', 'flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] border border-[color-mix(in_srgb,var(--bad)_55%,var(--line))] bg-surface-1 px-4 font-bold text-bad outline-none transition-colors duration-[var(--t-hover)] hover:bg-[color-mix(in_srgb,var(--bad)_8%,var(--bg-1))] focus-visible:shadow-[0_0_0_2px_var(--accent)]')}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h11" /></svg>
          Leave party
        </button>
      ) : null}
    </div>
  );

  const liveScreens = voice.partyId === id ? (
    <div className="flex-none overflow-hidden rounded-[var(--radius-xl)] border border-line-soft">
      <ScreenStage />
    </div>
  ) : null;

  if (fit) {
    return (
      <>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="mx-auto flex min-h-0 w-full max-w-[1480px] flex-1 gap-3 px-4 pt-4 sm:px-6 xl:px-8">
            <div ref={columnRef} className={cn('flex min-h-0 min-w-0 flex-1 flex-col gap-3 pb-3', liveScreens ? 'overflow-y-auto overscroll-contain' : 'overflow-hidden')}>
              {topRow}
              {hero}
              {liveScreens}
              <div className="flex flex-none flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1"><ReadyCheck party={party} canManage={canManage} /></div>
                <RaiseHand party={party} />
                <BackSoon party={party} />
              </div>
              {memberRow}
              {chat}
            </div>
            {/* Drag the rail narrower and the room - chat included - grows. */}
            <Grip dragging={railSplit.dragging} label="Resize the party details" className="mt-12 mb-3" {...railSplit.gripProps} />
            <aside
              ref={railSplit.panelRef as React.RefObject<HTMLElement>}
              style={{ width: railSplit.width }}
              aria-label="About this party"
              className={cn(
                'flex min-h-0 flex-none flex-col overflow-x-hidden overflow-y-auto pt-12 pb-3',
                railSplit.dragging ? 'transition-none' : 'transition-[width] duration-[250ms] ease-[var(--ease-out)] motion-reduce:transition-none'
              )}
            >
              {rail}
            </aside>
          </div>
          <CallStrip party={party} inVoice={inVoice} connected={connectedHere} />
        </div>
      {heroMenu ? <MenuPanel at={heroMenu} items={heroItems} onClose={() => setHeroMenu(null)} /> : null}

      {editing ? <EditParty party={party} onClose={() => setEditing(false)} /> : null}
      {perms ? <PartyPermissions party={party} onClose={() => setPerms(false)} /> : null}
      {logOpen ? <PartyLog partyId={id} onClose={() => setLogOpen(false)} /> : null}

      {tuning ? (
        <Modal
          title={'Volume for ' + (nicknames[tuning.id] || tuning.name)}
          subtitle="Only you hear this. Nobody is told, and nobody else is affected."
          onClose={() => setTuning(null)}
          footer={<Button kind="outline" onClick={() => setTuning(null)}>Done</Button>}
        >
          <Field label={Math.round((voice.levels[tuning.id] ?? 1) * 100) + '%'}>
            <input
              type="range"
              min={0}
              max={200}
              value={Math.round((voice.levels[tuning.id] ?? 1) * 100)}
              onChange={(e) => voice.setLevel(tuning.id, Number(e.target.value) / 100)}
              className="w-full accent-[var(--accent)]"
            />
          </Field>
          <p className="text-[12.5px] text-mute">
            Above 100% amplifies someone quiet. It can clip if you push it far.
          </p>
        </Modal>
      ) : null}

      {renaming ? (
        <NicknameDialog
          userId={renaming.id}
          realName={renaming.name}
          current={nicknames[renaming.id] ?? ''}
          onSave={(v) => { setNickname(renaming.id, v); setRenaming(null); }}
          onClose={() => setRenaming(null)}
        />
      ) : null}
      {inviting ? <InvitePeople partyId={id} canManage={canManage} onClose={() => setInviting(false)} /> : null}
      {managing ? (
        <ChannelDialog
          partyId={id}
          channel={managing.channel}
          onClose={() => setManaging(null)}
        />
      ) : null}
      {dialog}
      </>
    );
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-5 px-4 py-5 sm:px-6">
          {topRow}
          {hero}
          {liveScreens}
          <ReadyCheck party={party} canManage={canManage} />
          <div role="tablist" aria-label="Party views" className="grid grid-cols-3 gap-1 rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft bg-surface-inset p-1">
            {([['chat', 'Chat'], ['members', 'Members ' + party.memberCount + '/' + party.size], ['details', 'Details']] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={mobileTab === key}
                onClick={() => setMobileTab(key)}
                className={cn(
                  'h-[40px] rounded-[calc(10px*var(--radius-mul,1))] text-[14px] font-semibold outline-none',
                  'transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  mobileTab === key ? 'bg-surface-1 text-accent shadow-[var(--shadow-1)]' : 'text-dim'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {mobileTab === 'members' ? memberRow : null}
          {mobileTab === 'chat' ? chat : null}
          {mobileTab === 'details' ? rail : null}
        </div>
      </div>
      <CallStrip party={party} inVoice={inVoice} connected={connectedHere} />
      {heroMenu ? <MenuPanel at={heroMenu} items={heroItems} onClose={() => setHeroMenu(null)} /> : null}

      {editing ? <EditParty party={party} onClose={() => setEditing(false)} /> : null}
      {perms ? <PartyPermissions party={party} onClose={() => setPerms(false)} /> : null}
      {logOpen ? <PartyLog partyId={id} onClose={() => setLogOpen(false)} /> : null}

      {tuning ? (
        <Modal
          title={'Volume for ' + (nicknames[tuning.id] || tuning.name)}
          subtitle="Only you hear this. Nobody is told, and nobody else is affected."
          onClose={() => setTuning(null)}
          footer={<Button kind="outline" onClick={() => setTuning(null)}>Done</Button>}
        >
          <Field label={Math.round((voice.levels[tuning.id] ?? 1) * 100) + '%'}>
            <input
              type="range"
              min={0}
              max={200}
              value={Math.round((voice.levels[tuning.id] ?? 1) * 100)}
              onChange={(e) => voice.setLevel(tuning.id, Number(e.target.value) / 100)}
              className="w-full accent-[var(--accent)]"
            />
          </Field>
          <p className="text-[12.5px] text-mute">
            Above 100% amplifies someone quiet. It can clip if you push it far.
          </p>
        </Modal>
      ) : null}

      {renaming ? (
        <NicknameDialog
          userId={renaming.id}
          realName={renaming.name}
          current={nicknames[renaming.id] ?? ''}
          onSave={(v) => { setNickname(renaming.id, v); setRenaming(null); }}
          onClose={() => setRenaming(null)}
        />
      ) : null}
      {inviting ? <InvitePeople partyId={id} canManage={canManage} onClose={() => setInviting(false)} /> : null}
      {managing ? (
        <ChannelDialog
          partyId={id}
          channel={managing.channel}
          onClose={() => setManaging(null)}
        />
      ) : null}
      {dialog}
    </>
  );
}

function rank(m: PartyMember, party: Party): number {
  return m.userId === party.hostId ? 0 : m.role === 'officer' ? 1 : 2;
}

/**
 * A member of the room.
 *
 * Replaces the roster row. That row was a button with more buttons inside it -
 * invalid, and a screen reader could not tell them apart - and the host's
 * controls only appeared on hover, so nobody on a touchscreen could reach
 * them. The card has one button for the profile and a menu button that is
 * always there; the menu is the same one right-click opens.
 */
function MemberCard({ member, party, inVoice, voiceState, menu, compact }: {
  member: PartyMember;
  party: Party;
  inVoice: boolean;
  /** Their live call state, when they are in voice: speaking, muted, sharing. */
  voiceState?: { muted: boolean; deafened: boolean; speaking: boolean; sharing: boolean };
  menu: (user: { id: string; username: string; displayName: string }) => MenuItem[];
  /** A small tile - picture beside the name - for a short window. */
  compact?: boolean;
}) {
  const me = useSession((s) => s.me);
  const user = member.user;
  const ctx = useContextMenu(() => (user ? menu(user) : []));
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  if (!user) return null;

  const host = member.userId === party.hostId;
  const officer = !host && member.role === 'officer';
  const self = me?.id === member.userId;
  const role = host ? 'Host' : officer ? 'Officer' : 'Member';

  return (
    <div
      {...ctx.props}
      className={cn(
        compact
          ? 'relative flex min-h-[64px] items-center gap-3 rounded-[var(--radius-l)] border bg-surface-1 py-2.5 pr-10 pl-2.5 text-left'
          : 'relative flex min-h-[150px] flex-col items-center gap-2 rounded-[var(--radius-l)] border bg-surface-1 px-3 pt-4 pb-3 text-center',
        'transition-[border-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)] hover:border-line',
        host ? 'border-[color-mix(in_srgb,var(--tier-mythic)_45%,var(--line))]' : 'border-line-soft'
      )}
    >
      {ctx.menu}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); openUserCard(user, e.currentTarget); }}
        aria-label={'Show ' + user.displayName + "'s profile card"}
        className="relative flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--bg-1),0_0_0_4px_var(--accent)]"
      >
        {/* A ring that lights while they talk, as in the reference. */}
        <span
          className={cn(
            'block rounded-full transition-[box-shadow] duration-150',
            compact ? 'p-[2px]' : 'p-[3px]',
            voiceState?.speaking && !voiceState.muted ? 'shadow-[0_0_0_3px_var(--good)]' : 'shadow-[0_0_0_3px_transparent]'
          )}
        >
          <Avatar user={user} size={compact ? 40 : 72} />
        </span>
        {inVoice ? (
          <span
            role="img"
            aria-label={voiceState?.deafened ? 'Deafened' : voiceState?.muted ? 'Muted' : voiceState?.speaking ? 'Speaking' : 'In voice'}
            className={cn(
              'absolute -right-0.5 -bottom-0.5 grid place-items-center rounded-full text-white ring-[var(--bg-1)]',
              compact ? 'h-5 w-5 ring-2 [&>svg]:h-[11px] [&>svg]:w-[11px]' : 'h-7 w-7 ring-[3px]',
              voiceState?.muted || voiceState?.deafened ? 'bg-ghost' : 'bg-good'
            )}
          >
            {voiceState?.muted || voiceState?.deafened ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M9 9v2a3 3 0 0 0 5.1 2.1M15 9.3V5a3 3 0 0 0-5.7-1.3M19 11a7 7 0 0 1-1.1 3.8M5 11a7 7 0 0 0 11.4 5.4M12 18v3M3 3l18 18" /></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M5 10v4M9 7v10M13 4v16M17 8v8M21 11v2" /></svg>
            )}
          </span>
        ) : null}
      </button>

      <span className={cn(compact ? 'flex min-w-0 flex-1 flex-col' : 'contents')}>
      <span className={cn('flex max-w-full min-w-0 items-center gap-1', !compact && 'justify-center')}>
        <UserName user={user} className={cn('truncate font-bold', compact ? 'text-[14px]' : 'text-[14.5px]')} />
        {host ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="flex-none text-mythic" role="img" aria-label="Host">
            <path d="M3.4 7.6 7 12l5-6.6L17 12l3.6-4.4-1.4 11H4.8Z" />
          </svg>
        ) : null}
      </span>
      <span className={cn('text-mute', compact ? 'truncate text-[12px]' : 'text-[12.5px]')}>
        {/* What they are doing in the call, where the reference puts it;
            their role otherwise. */}
        {inVoice
          ? (voiceState?.deafened ? 'Deafened' : voiceState?.muted ? 'Muted' : voiceState?.sharing ? 'Sharing screen' : voiceState?.speaking ? 'Speaking' : 'Not speaking')
          : role + (self ? ' · You' : '')}
      </span>
      </span>

      <button
        type="button"
        aria-label={'Options for ' + user.displayName}
        aria-haspopup="menu"
        onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setAt({ x: Math.max(8, r.right - 214), y: r.bottom + 4 }); }}
        className={cn(compact ? 'top-1/2 right-1.5 -translate-y-1/2' : 'top-2 right-2', 'absolute grid h-8 w-8 place-items-center rounded-[calc(8px*var(--radius-mul,1))] text-mute outline-none transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)] hover:bg-surface-3 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]')}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
      </button>
      {at ? <MenuPanel at={at} items={menu(user)} onClose={() => setAt(null)} /> : null}
    </div>
  );
}

/** Details, channels and the description, beside the room or below it. */
function PartyRail({ party, gameName, regionName, owner, fill, onInvite, addons, shared }: {
  party: Party;
  gameName: string;
  regionName: string;
  owner: PartyMember['user'];
  /** Fills the rail's height, with the addons taking what the details leave. */
  fill?: boolean;
  onInvite?: () => void;
  addons: React.ReactNode;
  /** What the room can all play, when there is a room to ask about. */
  shared?: React.ReactNode;
}) {
  const toast = useToast();
  /* The facts fold away, remembered, to give the addons below more room. */
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('pogu.party.details') !== '0'; } catch { return true; } });
  const flip = () => setOpen((v) => { try { localStorage.setItem('pogu.party.details', v ? '0' : '1'); } catch { /* storage off */ } return !v; });
  /* Small facts in two columns: what you need at a glance, not a long list. */
  const facts: { label: string; value: string }[] = [
    { label: 'Mode', value: party.mode || 'Any' },
    { label: 'Region', value: regionName || 'Anywhere' },
    { label: 'Language', value: party.language || 'Any' },
    { label: 'Mic', value: party.micRequired ? 'Required' : 'Optional' },
    { label: 'Visibility', value: party.visibility === 'public' ? 'Public' : party.visibility === 'friends' ? 'Friends' : 'Invite only' },
    { label: 'Started', value: ago(party.createdAt) }
  ];
  const art = gameBanner(party.gameId);
  const seats = Math.max(0, party.size - party.memberCount);

  return (
    <div className={cn('flex flex-col gap-3', fill && 'min-h-full')}>
      <section aria-labelledby="party-details-title" className={cn(RAIL_CARD, 'flex-none p-3.5')}>
        <div className="flex items-center gap-3">
          <span className="smooth-clip h-12 w-12 flex-none overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] bg-surface-3">
            {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : null}
          </span>
          <span className="min-w-0 flex-1">
            <h2 id="party-details-title" className="m-0 truncate text-[15px] font-bold text-ink">{gameName}</h2>
            <span className="flex items-center gap-1.5 text-[12.5px] text-mute">
              <Icon name="users" size={13} />
              {party.memberCount}/{party.size}
              <span>{seats ? '· ' + seats + ' free' : '· full'}</span>
            </span>
          </span>
          {owner ? (
            <button
              type="button"
              onClick={(e) => openUserCard(owner, e.currentTarget)}
              title={'Host: ' + owner.displayName}
              className="relative flex-none rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <Avatar user={owner} size={32} />
              <span className="absolute -top-1.5 -right-1 grid h-4 w-4 place-items-center rounded-full bg-surface-1 text-warn"><Icon name="crown" size={11} /></span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={flip}
            aria-expanded={open}
            aria-label={open ? 'Hide party details' : 'Show party details'}
            className="grid h-8 w-8 flex-none place-items-center rounded-[9px] text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            <Icon name="chevronD" size={16} className={cn('transition-transform duration-200', !open && '-rotate-90')} />
          </button>
        </div>

        {open ? (<>

        <dl className="m-0 mt-2.5 flex flex-wrap gap-1.5">
          {facts.map((f) => (
            <div key={f.label} title={f.label} className="flex min-w-0 items-baseline gap-1 rounded-full bg-surface-2 px-2.5 py-1 text-[12px]">
              <dt className="text-mute">{f.label}</dt>
              <dd className="m-0 truncate font-semibold text-ink">{f.value}</dd>
            </div>
          ))}
        </dl>

        {party.description ? (
          <p className="m-0 mt-2.5 line-clamp-3 text-[12.5px] leading-snug text-dim"><EmojiText text={party.description} size={14} /></p>
        ) : null}
        {party.tags?.length ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {party.tags.map((t) => <span key={t} className="rounded-full bg-surface-2 px-2 py-0.5 text-[11.5px] font-semibold text-dim">{t}</span>)}
          </div>
        ) : null}
        </>) : null}

        {onInvite ? (
          <div className="mt-3 flex gap-2">
            <Button kind="primary" size="sm" className="h-9 flex-1" icon={<Icon name="addFriend" size={15} />} onClick={onInvite}>Invite</Button>
            <Button
              kind="outline"
              size="sm"
              className="h-9 flex-1"
              icon={<Icon name="copy" size={14} />}
              onClick={() => { void copyText(location.origin + '/#/party/' + party.id).then((ok) => toast(ok ? 'Link copied' : 'Could not copy', ok ? 'good' : 'bad')); }}
            >
              Copy link
            </Button>
          </div>
        ) : null}
      </section>

      {shared}
      {addons}
    </div>
  );
}

/* A rail card: soft, rounded, edged with a line rather than a border. */
const RAIL_CARD = 'pugo-panel rounded-[calc(20px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]';

/** Shaped like the room, so nothing jumps when it arrives. */
function PartySkeleton() {
  return (
    <div className="min-h-0 flex-1 overflow-hidden" aria-busy="true" aria-label="Loading party">
      <div className="mx-auto flex w-full max-w-[1440px] gap-6 px-4 py-5 sm:px-6 xl:px-8">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <div className="h-5 w-32 animate-pulse rounded bg-surface-3" />
          <div className="h-[176px] animate-pulse rounded-[var(--radius-xl)] bg-surface-3" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-[150px] animate-pulse rounded-[var(--radius-l)] bg-surface-3" />)}
          </div>
          <div className="h-[420px] animate-pulse rounded-[var(--radius-xl)] bg-surface-3" />
        </div>
      </div>
    </div>
  );
}

/**
 * The call, along the bottom of the room.
 *
 * As the reference draws it: who you are connected to on the left, the three
 * round controls in the middle - microphone, headphones, screen - and Leave
 * voice on the right where it cannot be pressed by accident. Sticky, so it
 * stays in reach however far down the chat you scroll. Same voice store as
 * the floating dock.
 */
function CallStrip({ party, inVoice, connected }: { party: Party; inVoice: Set<string>; connected: boolean }) {
  const voice = useVoice();
  const toast = useToast();
  /* Not in the call yet: the same bar, as the way in. */
  if (!connected) {
    return (
      <div className="flex-none border-t border-line-soft bg-surface-0 px-4 py-3 sm:px-6 xl:px-8">
        <VoiceBar party={party} inVoice={inVoice} connected={false} />
      </div>
    );
  }
  const round = (on: boolean) => cn(
    'grid h-12 w-12 place-items-center rounded-[calc(14px*var(--radius-mul,1))] border outline-none',
    'transition-[background-color,border-color,color] duration-[var(--t-hover)] ease-[var(--ease)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
    on ? 'border-transparent bg-ink text-surface-1' : 'border-line-soft bg-surface-1 text-ink hover:border-line hover:bg-surface-2'
  );

  async function share() {
    try {
      if (voice.sharing) voice.stopScreenShare();
      else if (!(await voice.startScreenShare(voice.shareQuality))) toast('Screen sharing did not start', 'warn');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <div
      role="region"
      aria-label="Voice call controls"
      className="flex flex-none flex-wrap items-center gap-3 border-t border-line-soft bg-surface-0 px-4 py-3 sm:px-6 xl:px-8"
    >
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="flex-none text-voice" aria-hidden="true">
          <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />
        </svg>
        <span className="min-w-0" role="status">
          <span className="block text-[15px] font-bold text-voice">
            {voice.deafened ? 'Deafened' : voice.muted ? 'Voice connected · muted' : 'Voice connected'}
          </span>
          <span className="block truncate text-[13px] text-mute">{party.title} · {party.memberCount}/{party.size}</span>
        </span>
      </span>

      <span className="flex items-center gap-2.5">
        <button
          type="button"
          aria-label={voice.muted ? 'Unmute microphone' : 'Mute microphone'}
          aria-pressed={voice.muted}
          disabled={voice.listenOnly}
          onClick={() => voice.toggleMute()}
          className={cn(round(!voice.muted && !voice.listenOnly), 'disabled:opacity-45')}
        >
          {voice.muted ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M9 9v2a3 3 0 0 0 5.1 2.1M15 9.3V5a3 3 0 0 0-5.7-1.3M19 11a7 7 0 0 1-1.1 3.8M5 11a7 7 0 0 0 11.4 5.4M12 18v3M3 3l18 18" /></svg>
          ) : (
            <Icon name="mic" size={20} />
          )}
        </button>
        <button
          type="button"
          aria-label={voice.deafened ? 'Undeafen' : 'Deafen'}
          aria-pressed={voice.deafened}
          onClick={() => voice.toggleDeafen()}
          className={round(voice.deafened)}
        >
          <Icon name="headset" size={20} />
        </button>
        <button
          type="button"
          aria-pressed={voice.sharing}
          onClick={() => void share()}
          className={cn(round(voice.sharing), 'w-auto gap-2 px-4 text-[14px] font-semibold', 'flex')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4M12 8v5M9.5 10.5 12 8l2.5 2.5" /></svg>
          {voice.sharing ? 'Stop sharing' : 'Share screen'}
        </button>
      </span>

      <span className="flex flex-1 justify-end">
        <button
          type="button"
          onClick={() => void voice.leave()}
          className="flex h-11 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] border border-[color-mix(in_srgb,var(--bad)_55%,var(--line))] bg-surface-1 px-4 text-[14px] font-bold text-bad outline-none transition-colors duration-[var(--t-hover)] hover:bg-[color-mix(in_srgb,var(--bad)_10%,var(--bg-1))] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 8c-3.9 0-7.4 1.3-9.6 3.4a1.3 1.3 0 0 0 0 1.8l2 2c.4.4 1 .5 1.5.2l2.2-1.2c.4-.2.7-.7.6-1.2l-.3-1.8c1.1-.4 2.3-.6 3.6-.6s2.5.2 3.6.6l-.3 1.8c-.1.5.2 1 .6 1.2l2.2 1.2c.5.3 1.1.2 1.5-.2l2-2a1.3 1.3 0 0 0 0-1.8C19.4 9.3 15.9 8 12 8Z" /></svg>
          Leave voice
        </button>
      </span>
    </div>
  );
}

function VoiceBar({ party, inVoice, connected }: { party: Party; inVoice: Set<string>; connected: boolean }) {
  const voice = useVoice();
  const toast = useToast();
  const [checking, setChecking] = useState(false);

  async function join() {
    try { await voice.join(party.id); }
    catch (err) { toast((err as Error).message, 'bad'); }
  }

  if (party.voice?.enabled === false) {
    return (
      <div className="flex items-center gap-3 rounded-[var(--radius-l)] border border-line-soft bg-surface-2 px-4 py-3">
        <span className="text-[14px] font-bold text-ink">Voice is off</span>
        <span className="text-[13px] text-mute">The host turned the voice channel off.</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-[var(--radius-l)] border px-4 py-3',
        connected ? 'border-[color-mix(in_srgb,var(--voice)_32%,var(--line))] bg-[color-mix(in_srgb,var(--voice)_8%,var(--bg-1))]' : 'border-line-soft bg-surface-1'
      )}
    >
      <span className={cn('grid h-9 w-9 flex-none place-items-center rounded-full', connected ? 'bg-[color-mix(in_srgb,var(--voice)_16%,transparent)] text-voice' : 'bg-surface-3 text-mute')}>
        <Icon name="headset" size={18} />
      </span>
      <span className="min-w-0 flex-1" role="status">
        <span className={cn('block text-[14px] font-bold', connected ? 'text-voice' : 'text-ink')}>
          {connected ? (voice.listenOnly ? 'Listening — no microphone' : voice.deafened ? 'Deafened' : voice.muted ? 'Voice connected · muted' : 'Voice connected') : 'Voice channel'}
        </span>
        <span className="block text-[13px] text-mute">
          {connected
            ? (voice.members.length === 1 ? 'Just you in the call' : voice.members.length + ' in the call')
            : inVoice.size ? (inVoice.size === 1 ? '1 person in voice' : inVoice.size + ' people in voice') : 'Nobody in voice yet'}
        </span>
      </span>

      {connected ? (
        /* The same store the floating dock uses - not a second copy of the
           state, just a second place to reach it. */
        <span className="flex items-center gap-2">
          <Button
            kind={voice.muted ? 'primary' : 'outline'}
            size="sm"
            disabled={voice.listenOnly}
            aria-pressed={voice.muted}
            onClick={() => voice.toggleMute()}
          >
            {voice.muted ? 'Unmute' : 'Mute'}
          </Button>
          <Button kind={voice.deafened ? 'primary' : 'outline'} size="sm" aria-pressed={voice.deafened} onClick={() => voice.toggleDeafen()}>
            {voice.deafened ? 'Undeafen' : 'Deafen'}
          </Button>
          <Button kind="danger" size="sm" onClick={() => void voice.leave()}>Leave voice</Button>
        </span>
      ) : (
        <Button
          kind="primary"
          size="sm"
          disabled={!party.isMember}
          onClick={() => {
            /* Check the microphone before anybody else is listening to it,
               unless this person has said they would rather not. */
            if (micCheckWanted()) { setChecking(true); return; }
            void join();
          }}
        >
          {party.isMember ? 'Join voice' : 'Join the party to talk'}
        </Button>
      )}
      {checking ? (
        <MicCheck
          onClose={() => setChecking(false)}
          onJoin={() => void join()}
        />
      ) : null}
    </div>
  );
}

/**
 * Everything about a party, in one dialog.
 *
 * Grouped the way you think about it rather than the way it is stored: what
 * the party is, who it is looking for, and how people get in. The size ceiling
 * comes from the server with your account, because the level that unlocks 20
 * and 50 is the server's rule and a form that guessed it would eventually
 * offer a number the save then refused.
 */
function EditParty({ party, onClose }: { party: Party; onClose: () => void }) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();
  const toast = useToast();

  const game = catalog?.games.find((g) => g.id === party.gameId);
  const cap = me?.partySizeCap ?? 5;

  const [title, setTitle] = useState(party.title);
  const [description, setDescription] = useState(party.description);
  const [mode, setMode] = useState(party.mode);
  const [region, setRegion] = useState(party.region);
  const [language, setLanguage] = useState(party.language ?? '');
  const [minRank, setMinRank] = useState(party.minRank ?? '');
  const [maxRank, setMaxRank] = useState(party.maxRank ?? '');
  const [needRoles, setNeedRoles] = useState<string[]>(party.needRoles ?? []);
  const [tags, setTags] = useState<string[]>(party.tags ?? []);
  const [tagDraft, setTagDraft] = useState('');
  const [size, setSize] = useState(party.size);
  const [style, setStyle] = useState(party.style ?? 'none');
  const [micRequired, setMicRequired] = useState(party.micRequired);
  const [visibility, setVisibility] = useState(party.visibility);
  const [textEnabled, setTextEnabled] = useState(party.textEnabled);
  const [voiceEnabled, setVoiceEnabled] = useState(party.voice?.enabled !== false);
  const [closed, setClosed] = useState(party.closed);
  const [busy, setBusy] = useState(false);

  // You cannot shrink a party below the people already in it, and you cannot
  // grow one past what your level allows. Both ends are shown rather than
  // silently corrected on save.
  const floor = Math.max(2, party.memberCount);

  function toggleRole(role: string) {
    setNeedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : prev.length < 6 ? [...prev, role] : prev
    );
  }

  function addTag() {
    const next = tagDraft.trim().slice(0, 20);
    if (!next || tags.includes(next) || tags.length >= 6) { setTagDraft(''); return; }
    setTags([...tags, next]);
    setTagDraft('');
  }

  async function save() {
    setBusy(true);
    try {
      await api.patch('/api/parties/' + party.id, {
        title, description, mode, region, language,
        minRank, maxRank, needRoles, tags, size, style,
        micRequired, visibility, textEnabled, voiceEnabled, closed
      });
      toast('Party updated', 'good');
      await queryClient.invalidateQueries({ queryKey: ['party', party.id] });
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      wide
      title="Edit party"
      subtitle={game?.name ?? party.gameId}
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-[calc(var(--gap)*1.2)]">
        <Group label="About">
          <Field label="Title">
            <EmojiField value={title} onChange={setTitle} maxLength={60} />
          </Field>
          <Field label="Description" hint={description.length + ' / 500'}>
            <EmojiField
              textarea
              value={description}
              onChange={setDescription}
              maxLength={500}
              placeholder="What are you playing, and what are you after?"
            />
          </Field>
          <div className="grid gap-[var(--gap)] sm:grid-cols-2">
            <Field label="Mode">
              <Select
                search={false}
                value={mode}
                onChange={setMode}
                options={(game?.modes ?? [party.mode]).map((m) => ({ value: m, label: m }))}
              />
            </Field>
            <Field label="Region">
              <Select
                lead="globe"
                value={region}
                onChange={setRegion}
                options={(catalog?.regions ?? []).map((r) => ({ value: r.id, label: r.name }))}
              />
            </Field>
          </div>
          <Field label="Language">
            <Select
              lead="translate"
              value={language}
              onChange={setLanguage}
              options={(catalog?.languages ?? []).map((l) => ({ value: l, label: l }))}
            />
          </Field>
        </Group>

        <Group label="Who you are looking for">
          <div className="grid gap-[var(--gap)] sm:grid-cols-2">
            <Field label="Lowest rank" hint="Leave on Any for an open lobby.">
              <Select
                lead="trending"
                value={minRank}
                onChange={setMinRank}
                options={[{ value: '', label: 'Any' }, ...(game?.ranks ?? []).map((r) => ({ value: r, label: r }))]}
              />
            </Field>
            <Field label="Highest rank">
              <Select
                lead="trending"
                value={maxRank}
                onChange={setMaxRank}
                options={[{ value: '', label: 'Any' }, ...(game?.ranks ?? []).map((r) => ({ value: r, label: r }))]}
              />
            </Field>
          </div>

          {game?.roles.length ? (
            <Field label="Roles you need" hint="Up to six. Leave empty if anything goes.">
              <div className="flex flex-wrap gap-1.5">
                {game.roles.map((role) => {
                  const on = needRoles.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleRole(role)}
                      className={cn(
                        'h-[var(--h-sm)] rounded-full border px-3 text-[12.5px] font-semibold',
                        'transition-[background,border-color,color] duration-150',
                        on
                          ? 'border-accent bg-[color-mix(in_srgb,var(--accent)_18%,transparent)] text-ink'
                          : 'border-line-soft bg-surface-2 text-mute hover:border-line hover:text-dim'
                      )}
                    >
                      {role}
                    </button>
                  );
                })}
              </div>
            </Field>
          ) : null}

          <Field label="Tags" hint="Up to six. Enter to add.">
            <div className="flex flex-wrap items-center gap-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full border border-line-soft bg-surface-2 py-1 pr-1.5 pl-2.5 text-[12px] font-semibold text-dim"
                >
                  {t}
                  <button
                    type="button"
                    aria-label={'Remove ' + t}
                    onClick={() => setTags(tags.filter((x) => x !== t))}
                    className="grid h-4 w-4 place-items-center rounded-[calc(6px*var(--radius-mul,1))] text-mute transition-colors hover:bg-surface-4 hover:text-bad"
                  >
                    <Icon name="close" size={10} stroke={2.4} />
                  </button>
                </span>
              ))}
              {tags.length < 6 ? (
                <Input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  onBlur={addTag}
                  placeholder="chill, ranked…"
                  maxLength={20}
                  className="h-[var(--h-sm)] w-[150px]"
                />
              ) : null}
            </div>
          </Field>
        </Group>

        <Group label="How the card looks">
          <StylePicker value={style} onChange={setStyle} />
        </Group>

        <Group label="Size and access">
          <Field
            label="Party size"
            /* Both ends come from the party and the account rather than being
               written out - what a plan is worth is set in the staff console
               now, so a sentence naming 5, 20 and 50 would go stale the first
               time somebody changed one. */
            hint={
              floor > 2
                ? cap + ' is your limit, and ' + floor + ' are already here.'
                : 'Up to ' + cap + ' on your account.'
            }
          >
            <NumberField value={size} min={floor} max={cap} onChange={setSize} suffix="players" />
          </Field>

          <Field label="Who can see it">
            <Select
              search={false}
              value={visibility}
              onChange={(v) => setVisibility(v as Party['visibility'])}
              options={[
                { value: 'public', label: 'Everyone', sub: 'Listed on the parties page', icon: 'globe' },
                { value: 'friends', label: 'Friends only', sub: 'Only your friends see it listed', icon: 'friends' },
                { value: 'invite', label: 'Invite only', sub: 'Hidden - people need an invite', icon: 'lock' }
              ]}
            />
          </Field>

          <div className="flex flex-col divide-y divide-line-soft">
            <Toggle
              checked={micRequired}
              onChange={setMicRequired}
              label="Microphone required"
              note="Shown on the card, so people know before they join."
            />
            <Toggle
              checked={voiceEnabled}
              onChange={setVoiceEnabled}
              label="Voice channels"
              note="Turning this off disconnects anyone currently talking."
            />
            <Toggle
              checked={textEnabled}
              onChange={setTextEnabled}
              label="Text channels"
            />
            <Toggle
              checked={closed}
              onChange={setClosed}
              label="Closed"
              note="Nobody new can join. The party stays open for the people already in it."
            />
          </div>
        </Group>
      </div>
    </Modal>
  );
}

/** One titled block of settings inside the dialog. */
function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-[var(--gap)]">
      <h4 className="text-[10.5px] font-bold tracking-[.09em] text-ghost uppercase">{label}</h4>
      {children}
    </section>
  );
}

/**
 * Adding, renaming or removing a channel.
 *
 * One dialog for all three: opened with a channel it edits that one, opened
 * with nothing it makes a new one. A party keeps at least one channel of each
 * kind, so the server refuses the last delete and says so.
 */
function ChannelDialog({ partyId, channel, onClose }: {
  partyId: string;
  channel: Channel | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(channel?.name ?? '');
  const [kind, setKind] = useState<'text' | 'voice'>(channel?.kind ?? 'voice');
  const [locked, setLocked] = useState(!!channel?.locked);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  async function save() {
    setBusy(true);
    try {
      if (channel) {
        await api.patch('/api/parties/' + partyId + '/channels/' + channel.id, { name, locked });
        toast('Channel updated', 'good');
      } else {
        await api.post('/api/parties/' + partyId + '/channels', { kind, name, locked });
        toast('Channel added', 'good');
      }
      await queryClient.invalidateQueries({ queryKey: ['party', partyId] });
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!channel) return;
    const ok = await confirm('Delete ' + channel.name + '?', 'Anyone inside is disconnected.', {
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await api.del('/api/parties/' + partyId + '/channels/' + channel.id);
      await queryClient.invalidateQueries({ queryKey: ['party', partyId] });
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <Modal
      title={channel ? 'Edit ' + channel.name : 'Add a channel'}
      onClose={onClose}
      footer={
        <>
          {channel ? (
            <Button kind="danger" onClick={() => void remove()}>Delete</Button>
          ) : null}
          <span className="flex-1" />
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {channel ? 'Save' : 'Add channel'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-[var(--gap)]">
        {!channel ? (
          <Field label="Kind">
            <Select
              search={false}
              value={kind}
              onChange={(v) => setKind(v as 'text' | 'voice')}
              options={[
                { value: 'voice', label: 'Voice channel', sub: 'Talk here', icon: 'volume' },
                { value: 'text', label: 'Text channel', sub: 'Type here', icon: 'hash' }
              ]}
            />
          </Field>
        ) : null}

        <Field label="Name" hint="Up to 24 characters.">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={24}
            placeholder={kind === 'voice' ? 'Squad voice' : 'strategy'}
          />
        </Field>

        <Toggle
          checked={locked}
          onChange={setLocked}
          label="Officers only"
          note="Members can see it but not join or post"
        />
      </div>
      {dialog}
    </Modal>
  );
}

/**
 * Renaming somebody, for yourself.
 *
 * The name lives on this device and is never sent anywhere: the person keeps
 * the name they chose, and only your client shows them differently. Clearing
 * the box gives them their own name back.
 */
function NicknameDialog({ userId, realName, current, onSave, onClose }: {
  userId: string;
  realName: string;
  current: string;
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(current);
  void userId;

  return (
    <Modal
      title={'Nickname for ' + realName}
      subtitle="Only you see this. They are not told, and it stays on this device."
      onClose={onClose}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          {current ? <Button kind="outline" onClick={() => onSave('')}>Reset</Button> : null}
          <Button data-primary kind="primary" onClick={() => onSave(value)}>Save</Button>
        </>
      }
    >
      <Field label="Nickname" hint={'Leave empty to call them ' + realName + ' again.'}>
        <Input
          autoFocus
          value={value}
          maxLength={32}
          placeholder={realName}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(value); }}
        />
      </Field>
    </Modal>
  );
}

/**
 * Choosing what the party wears in the browse list.
 *
 * Each swatch is the real style on a small card, so what you pick is what you
 * get - a list of names would tell you nothing about what "Prism" looks like.
 * Styles above your membership are shown rather than hidden: knowing a thing
 * exists is the point of it being a perk.
 */
function StylePicker({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const tier = me?.tier ?? 'none';
  const styles = catalog?.partyStyles ?? [];

  function allowed(styleTier: string): boolean {
    if (styleTier === 'free') return true;
    if (styleTier === 'premium') return tier === 'premium' || tier === 'vip';
    return tier === 'vip';
  }

  if (!styles.length) return <p className="text-[12.5px] text-mute">Styles are still loading.</p>;

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {styles.map((s) => {
        const can = allowed(s.tier);
        const on = value === s.id;
        return (
          <Tip key={s.id} text={s.name} sub={can ? s.desc : s.desc + ' — needs ' + (s.tier === 'vip' ? 'VIP' : 'Premium')}>
            <button
              type="button"
              disabled={!can}
              onClick={() => onChange(s.id)}
              className={cn(
                'group relative flex w-full flex-col gap-1.5 rounded-[calc(12px*var(--radius-mul,1))] border p-2 text-left',
                'transition-[border-color,background] duration-150',
                !can && 'cursor-not-allowed opacity-45',
                on ? 'border-accent bg-accent-soft' : 'border-line-soft bg-surface-1 hover:border-line'
              )}
            >
              {/* the style itself, on a card-shaped swatch */}
              <span
                data-pstyle={s.id !== 'none' ? s.id : undefined}
                className="relative block h-[46px] w-full overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-[linear-gradient(120deg,var(--bg-2),var(--bg-3))]"
              />
              <span className="flex items-center gap-1.5">
                <span className="min-w-0 flex-1 truncate text-[12px] font-bold">{s.name}</span>
                {s.tier !== 'free' ? (
                  <span
                    className={cn(
                      'flex-none rounded px-1 text-[9px] font-extrabold tracking-[.06em]',
                      s.tier === 'vip'
                        ? 'bg-[color-mix(in_srgb,var(--tier-mythic)_22%,transparent)] text-mythic'
                        : 'bg-[color-mix(in_srgb,var(--tier-epic)_22%,transparent)] text-epic'
                    )}
                  >
                    {s.tier === 'vip' ? 'VIP' : 'PREMIUM'}
                  </span>
                ) : null}
              </span>
            </button>
          </Tip>
        );
      })}
    </div>
  );
}
